import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Box, Grid, Button, Select, MenuItem, FormControl, InputLabel,
  TableBody, TableCell, TableHead, TableRow, Chip,
  TextField, Tabs, Tab, Card, CardContent
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import axios from '../../api/client';
import { useCurrency } from '../../context/CurrencyContext';
import { toast } from 'react-toastify';
import ScrollTable, { EMBEDDED_MAX_HEIGHT } from '../ScrollTable';
import { downloadWorkbook } from '../../utils/excelExport';
import { agencyReportSheets, reportFilename } from '../../utils/reportWorkbooks';

const AgencyReports = () => {
  const [landlords, setLandlords] = useState([]);
  const [properties, setProperties] = useState([]);
  const [selectedLandlord, setSelectedLandlord] = useState('');
  const [selectedProperty, setSelectedProperty] = useState('');
  const [startDate, setStartDate] = useState(new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [ageThreshold, setAgeThreshold] = useState(30);
  const [activeTab, setActiveTab] = useState(0);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchLandlords();
    fetchProperties();
  }, []);

  useEffect(() => {
    if (selectedLandlord) {
      fetchPropertiesByLandlord(selectedLandlord);
    } else {
      fetchProperties();
    }
  }, [selectedLandlord]);

  const fetchLandlords = async () => {
    try {
      const res = await axios.get('/landlords');
      setLandlords(res.data);
    } catch (error) {
      toast.error('Error fetching landlords');
    }
  };

  const fetchProperties = async () => {
    try {
      const res = await axios.get('/properties');
      setProperties(res.data);
    } catch (error) {
      toast.error('Error fetching properties');
    }
  };

  const fetchPropertiesByLandlord = async (landlordId) => {
    try {
      const res = await axios.get(`/landlords/${landlordId}/properties`);
      setProperties(res.data);
    } catch (error) {
      toast.error('Error fetching properties');
    }
  };

  const handleGenerateReport = async () => {
    setLoading(true);
    try {
      let endpoint = '';
      const params = new URLSearchParams();

      switch (activeTab) {
        case 0: // Dashboard Summary
          endpoint = `/agency-reports/dashboard-summary`;
          break;
        case 1: // Tenant Statistics
          endpoint = `/agency-reports/tenant-statistics`;
          if (selectedLandlord) params.append('landlordId', selectedLandlord);
          if (selectedProperty) params.append('propertyId', selectedProperty);
          break;
        case 2: // Landlord Portfolio
          endpoint = `/agency-reports/landlord-portfolio`;
          if (selectedLandlord) params.append('landlordId', selectedLandlord);
          break;
        case 3: // Payments
          endpoint = `/agency-reports/payments`;
          if (selectedLandlord) params.append('landlordId', selectedLandlord);
          if (selectedProperty) params.append('propertyId', selectedProperty);
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          break;
        case 4: // Arrears
          endpoint = `/agency-reports/arrears`;
          params.append('ageThreshold', ageThreshold);
          break;
        case 5: // Management Fee Revenue
          endpoint = `/agency-reports/management-fee-revenue`;
          if (selectedLandlord) params.append('landlordId', selectedLandlord);
          if (selectedProperty) params.append('propertyId', selectedProperty);
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          break;
        case 6: // Maintenance Efficiency
          endpoint = `/agency-reports/maintenance-efficiency`;
          if (selectedProperty) params.append('propertyId', selectedProperty);
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          break;
        case 7: // Portfolio Churn
          endpoint = `/agency-reports/portfolio-churn`;
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          break;
        case 8: // Agency Fees Collections
          endpoint = `/agency-reports/agency-fees-collections`;
          if (selectedLandlord) params.append('landlordId', selectedLandlord);
          if (selectedProperty) params.append('propertyId', selectedProperty);
          if (startDate) params.append('startDate', startDate);
          if (endDate) params.append('endDate', endDate);
          break;
        case 9: // Monthly Outstanding Collection
          if (!startDate || !endDate) {
            toast.error('Choose a start and end date');
            return;
          }
          endpoint = `/agency-reports/outstanding-collection`;
          if (selectedLandlord) params.append('landlordId', selectedLandlord);
          if (selectedProperty) params.append('propertyId', selectedProperty);
          params.append('startDate', startDate);
          params.append('endDate', endDate);
          break;
        default:
          // A tab with no report behind it. Bailing out here rather than falling through
          // to the request below, which with an empty endpoint would fetch the app's own
          // base URL and hand whatever came back to the report renderer.
          toast.error('That report is not available');
          return;
      }

      const res = await axios.get(`${endpoint}?${params.toString()}`);
      setReportData(res.data);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Error generating report');
    } finally {
      setLoading(false);
    }
  };

  const { formatMoney } = useCurrency();
  const formatCurrency = (amount) => formatMoney(amount, { decimals: 0 });

  // One slug per tab, in tab order -- the same names the API routes use.
  const AGENCY_REPORT_SLUGS = [
    'dashboard-summary', 'tenant-statistics', 'landlord-portfolio', 'payments', 'arrears',
    'management-fee-revenue', 'maintenance-efficiency', 'portfolio-churn',
    'agency-fees-collections', 'outstanding-collection'
  ];
  // The tabs that take a date range, so the filename can say which period it covers.
  const DATED_TABS = [3, 5, 6, 7, 8, 9];

  // Excel, built from the data already on screen: every list becomes a sheet, every total
  // a Summary row, and the grouped collection report comes out as one flat table.
  const handleDownloadExcel = async () => {
    if (!reportData) return;
    const slug = AGENCY_REPORT_SLUGS[activeTab];
    const landlordName = selectedLandlord ? (landlords.find((l) => l._id === selectedLandlord)?.name || null) : null;
    const dated = DATED_TABS.includes(activeTab);
    try {
      await downloadWorkbook({
        filename: reportFilename(slug, {
          landlordName,
          startDate: dated ? startDate : null,
          endDate: dated ? endDate : null
        }),
        sheets: agencyReportSheets(slug, reportData)
      });
    } catch (error) {
      toast.error('Could not build the Excel file');
      console.error('Excel export failed:', error);
    }
  };

  const renderArrears = () => {
    if (!reportData) return null;

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Arrears</Typography>
                <Typography variant="h4" color="error">
                  {formatCurrency(reportData.summary?.totalArrears || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Tenants</Typography>
                <Typography variant="h4">{reportData.summary?.totalTenants || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>By Age Bucket</Typography>
                <Box display="flex" gap={2}>
                  <Box>
                    <Typography variant="body2">0-30 days</Typography>
                    <Typography variant="h6">{formatCurrency(reportData.summary?.buckets?.['0-30'] || 0)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2">30-60 days</Typography>
                    <Typography variant="h6">{formatCurrency(reportData.summary?.buckets?.['30-60'] || 0)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2">60-90 days</Typography>
                    <Typography variant="h6">{formatCurrency(reportData.summary?.buckets?.['60-90'] || 0)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="body2">90+ days</Typography>
                    <Typography variant="h6" color="error">
                      {formatCurrency(reportData.summary?.buckets?.['90+'] || 0)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT}>
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Property</TableCell>
                <TableCell>Invoice</TableCell>
                <TableCell>Due Date</TableCell>
                <TableCell>Days Overdue</TableCell>
                <TableCell>Age Bucket</TableCell>
                <TableCell>Balance</TableCell>
                <TableCell>Last Payment</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.arrears?.map((item, idx) => (
                <TableRow key={idx}>
                  <TableCell>{item.tenantName}</TableCell>
                  <TableCell>{item.propertyName}</TableCell>
                  <TableCell>{item.invoiceNumber}</TableCell>
                  <TableCell>{new Date(item.dueDate).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <Chip
                      label={item.daysOverdue}
                      color={item.daysOverdue > 90 ? 'error' : item.daysOverdue > 60 ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={item.ageBucket}
                      color={item.ageBucket === '90+' ? 'error' : item.ageBucket === '60-90' ? 'warning' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatCurrency(item.balance)}</TableCell>
                  <TableCell>
                    {item.lastPaymentDate ? new Date(item.lastPaymentDate).toLocaleDateString() : 'Never'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
      </Box>
    );
  };

  const renderManagementFeeRevenue = () => {
    if (!reportData) return null;

    return (
      <Box>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Total Management Fee Revenue</Typography>
            <Typography variant="h3" color="success.main">
              {formatCurrency(reportData.totalRevenue || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Across {reportData.totalProperties || 0} properties from {reportData.totalLandlords || 0} landlords
            </Typography>
          </CardContent>
        </Card>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>By Landlord</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Landlord</TableCell>
                    <TableCell align="right">Properties</TableCell>
                    <TableCell align="right">Total Fees</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.byLandlord?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.landlordName}</TableCell>
                      <TableCell align="right">{item.propertyCount}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalFees)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>By Property</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell>Landlord</TableCell>
                    <TableCell align="right">Total Fees</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.byProperty?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.propertyName}</TableCell>
                      <TableCell>{item.landlordName}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalFees)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderMaintenanceEfficiency = () => {
    if (!reportData) return null;

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Repairs</Typography>
                <Typography variant="h4">{reportData.overallStats?.totalRepairs || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Cost</Typography>
                <Typography variant="h4">{formatCurrency(reportData.overallStats?.totalCost || 0)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Avg per Repair</Typography>
                <Typography variant="h4">{formatCurrency(reportData.overallStats?.averageCostPerRepair || 0)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Avg per Property</Typography>
                <Typography variant="h4">{formatCurrency(reportData.overallStats?.averageCostPerProperty || 0)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>By Property</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell align="right">Repairs</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                    <TableCell align="right">Avg Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.byProperty?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.propertyName}</TableCell>
                      <TableCell align="right">{item.repairCount}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalCost)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.averageCost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>By Repair Type</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Repair Type</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Total Cost</TableCell>
                    <TableCell align="right">Avg Cost</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.byType?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.repairType}</TableCell>
                      <TableCell align="right">{item.repairCount}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalCost)}</TableCell>
                      <TableCell align="right">{formatCurrency(item.averageCost)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderDashboardSummary = () => {
    if (!reportData) return null;

    const summaryCard = (title, value, options = {}) => {
      const { color, bgColor, borderColor, subtitle } = options;
      return (
        <Card
          sx={{
            height: '100%',
            borderLeft: borderColor ? `4px solid` : undefined,
            borderLeftColor: borderColor || undefined,
            bgcolor: bgColor || undefined,
            boxShadow: bgColor ? 0 : 1,
          }}
        >
          <CardContent>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="bold" color={color || 'text.primary'}>
              {value}
            </Typography>
            {subtitle != null && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {subtitle}
              </Typography>
            )}
          </CardContent>
        </Card>
      );
    };

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Typography variant="h5" fontWeight="bold" gutterBottom color="primary.main">
              Portfolio Overview
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Total Landlords', reportData.portfolio?.totalLandlords ?? 0, {
              bgColor: 'primary.light',
              borderColor: 'primary.main',
            })}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Total Properties', reportData.portfolio?.totalProperties ?? 0, {
              bgColor: 'primary.light',
              borderColor: 'primary.main',
            })}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Total Units', reportData.portfolio?.totalUnits ?? 0, {
              bgColor: 'info.light',
              borderColor: 'info.main',
            })}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Occupancy Rate', `${reportData.operations?.occupancyRate ?? 0}%`, {
              color: 'success.dark',
              bgColor: 'success.light',
              borderColor: 'success.main',
            })}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Total Tenants', reportData.portfolio?.totalTenants ?? 0, {
              bgColor: 'info.light',
              borderColor: 'info.main',
            })}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Active Tenants', reportData.portfolio?.activeTenants ?? 0, {
              color: 'success.dark',
              bgColor: 'success.light',
              borderColor: 'success.main',
            })}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Vacancy Rate', `${reportData.portfolio?.vacancyRate ?? 0}%`, {
              color: 'warning.dark',
              bgColor: 'warning.light',
              borderColor: 'warning.main',
            })}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Occupied Units', reportData.portfolio?.occupiedUnits ?? 0, {
              bgColor: 'info.light',
              borderColor: 'info.main',
            })}
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12}>
            <Typography variant="h5" fontWeight="bold" gutterBottom color="primary.main">
              Financial Overview
            </Typography>
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Monthly Revenue', formatCurrency(reportData.financial?.monthlyRevenue ?? 0), {
              color: 'success.dark',
              bgColor: 'success.light',
              borderColor: 'success.main',
            })}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Monthly Payments', formatCurrency(reportData.financial?.monthlyPayments ?? 0), {
              color: 'success.dark',
              bgColor: 'success.light',
              borderColor: 'success.main',
              subtitle: `${reportData.financial?.monthlyPaymentCount ?? 0} payments`,
            })}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Monthly Expenses', formatCurrency(reportData.financial?.monthlyExpenses ?? 0), {
              color: 'error.dark',
              bgColor: 'error.light',
              borderColor: 'error.main',
              // Said out loud because the figure changed meaning. It used to sum the
              // expenses table, which left out every cost nobody recorded as an expense —
              // transfer charges most of all. It is now the same total the profit and loss
              // reports, so the two agree.
              subtitle: 'Every agency cost in the ledger, recorded or not',
            })}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard(
              'Net Monthly Income',
              formatCurrency(reportData.financial?.netMonthlyIncome ?? 0),
              (reportData.financial?.netMonthlyIncome ?? 0) >= 0
                ? { color: 'success.dark', bgColor: 'success.light', borderColor: 'success.main' }
                : { color: 'error.dark', bgColor: 'error.light', borderColor: 'error.main' }
            )}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('YTD Revenue', formatCurrency(reportData.financial?.ytdRevenue ?? 0), {
              color: 'success.dark',
              bgColor: 'success.light',
              borderColor: 'success.main',
            })}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Outstanding Balance', formatCurrency(reportData.financial?.outstandingBalance ?? 0), {
              color: 'error.dark',
              bgColor: 'error.light',
              borderColor: 'error.main',
              subtitle: `${reportData.financial?.outstandingInvoiceCount ?? 0} invoices`,
            })}
          </Grid>
          <Grid item xs={12} md={3}>
            {summaryCard('Pending Expenses', reportData.operations?.pendingExpenses ?? 0, {
              color: 'warning.dark',
              bgColor: 'warning.light',
              borderColor: 'warning.main',
            })}
          </Grid>
        </Grid>
      </Box>
    );
  };

  const renderTenantStatistics = () => {
    if (!reportData) return null;

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Tenants</Typography>
                <Typography variant="h4">{reportData.summary?.totalTenants || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Active Tenants</Typography>
                <Typography variant="h4" color="success.main">
                  {reportData.summary?.activeTenants || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">With Outstanding</Typography>
                <Typography variant="h4" color="error">
                  {reportData.summary?.tenantsWithBalance || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Outstanding</Typography>
                <Typography variant="h4" color="error">
                  {formatCurrency(reportData.summary?.totalOutstanding || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>By Property</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell align="right">Tenants</TableCell>
                    <TableCell align="right">Total Balance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.byProperty?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.propertyName}</TableCell>
                      <TableCell align="right">{item.count}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalBalance)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>By Lease Status</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Count</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>Active</TableCell>
                    <TableCell align="right">{reportData.byLeaseStatus?.active || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Expiring Soon (≤30 days)</TableCell>
                    <TableCell align="right">{reportData.byLeaseStatus?.expiring_soon || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Expired</TableCell>
                    <TableCell align="right">{reportData.byLeaseStatus?.expired || 0}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Month-to-Month</TableCell>
                    <TableCell align="right">{reportData.byLeaseStatus?.month_to_month || 0}</TableCell>
                  </TableRow>
                </TableBody>
              </ScrollTable>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom>Tenant Details</Typography>
        <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Tenant</TableCell>
                <TableCell>Property</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Monthly Rent</TableCell>
                <TableCell>Lease End</TableCell>
                <TableCell align="right">Outstanding</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.tenants?.slice(0, 50).map((tenant, idx) => (
                <TableRow key={idx}>
                  <TableCell>{tenant.tenantName}</TableCell>
                  <TableCell>{tenant.propertyName}</TableCell>
                  <TableCell>{tenant.unitNumber || '-'}</TableCell>
                  <TableCell>{formatCurrency(tenant.monthlyRent)}</TableCell>
                  <TableCell>
                    {tenant.leaseEndDate ? new Date(tenant.leaseEndDate).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell align="right">
                    {tenant.outstandingBalance > 0 ? (
                      <Typography color="error">{formatCurrency(tenant.outstandingBalance)}</Typography>
                    ) : (
                      <Chip label="Paid" color="success" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
      </Box>
    );
  };

  const renderLandlordPortfolio = () => {
    if (!reportData) return null;

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Landlords</Typography>
                <Typography variant="h4">{reportData.summary?.totalLandlords || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Properties</Typography>
                <Typography variant="h4">{reportData.summary?.totalProperties || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Units</Typography>
                <Typography variant="h4">{reportData.summary?.totalUnits || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Overall Occupancy</Typography>
                <Typography variant="h4" color="success.main">
                  {reportData.summary?.overallOccupancyRate || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom>Landlord Details</Typography>
        <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }}>
            <TableHead>
              <TableRow>
                <TableCell>Landlord</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Properties</TableCell>
                <TableCell align="right">Units</TableCell>
                <TableCell align="right">Occupied</TableCell>
                <TableCell align="right">Occupancy %</TableCell>
                <TableCell align="right">Monthly Rent</TableCell>
                <TableCell align="right">Monthly Revenue</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.landlords?.map((landlord, idx) => (
                <TableRow key={idx}>
                  <TableCell>{landlord.landlordName}</TableCell>
                  <TableCell>
                    <Chip
                      label={landlord.isActive ? 'Active' : 'Inactive'}
                      color={landlord.isActive ? 'success' : 'error'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">{landlord.totalProperties}</TableCell>
                  <TableCell align="right">{landlord.totalUnits}</TableCell>
                  <TableCell align="right">{landlord.occupiedUnits}</TableCell>
                  <TableCell align="right">{landlord.occupancyRate}%</TableCell>
                  <TableCell align="right">{formatCurrency(landlord.actualMonthlyRent)}</TableCell>
                  <TableCell align="right">{formatCurrency(landlord.monthlyRevenue)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </ScrollTable>
      </Box>
    );
  };

  const renderPayments = () => {
    if (!reportData) return null;

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Payments</Typography>
                <Typography variant="h4" color="success.main">
                  {formatCurrency(reportData.summary?.totalPayments || 0)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6">Payment Count</Typography>
                <Typography variant="h4">{reportData.summary?.totalPaymentCount || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6">Average Payment</Typography>
                <Typography variant="h4">{formatCurrency(reportData.summary?.averagePayment || 0)}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>By Payment Type</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.byType?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.paymentType}</TableCell>
                      <TableCell align="right">{item.paymentCount}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>By Property</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.byProperty?.slice(0, 10).map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.propertyName}</TableCell>
                      <TableCell align="right">{item.paymentCount}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="h6" gutterBottom>By Landlord</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Landlord</TableCell>
                    <TableCell align="right">Count</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.byLandlord?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.landlordName}</TableCell>
                      <TableCell align="right">{item.paymentCount}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
          </Grid>
        </Grid>

        {reportData.monthlyTrend && reportData.monthlyTrend.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>Monthly Trend</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell align="right">Payment Count</TableCell>
                    <TableCell align="right">Total Amount</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.monthlyTrend.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.period}</TableCell>
                      <TableCell align="right">{item.paymentCount}</TableCell>
                      <TableCell align="right">{formatCurrency(item.totalAmount)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
          </Box>
        )}
      </Box>
    );
  };

  const renderPortfolioChurn = () => {
    if (!reportData) return null;

    return (
      <Box>
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Landlords</Typography>
                <Typography variant="h4">{reportData.summary?.totalLandlords || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Active</Typography>
                <Typography variant="h4" color="success.main">
                  {reportData.summary?.activeLandlords || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Inactive</Typography>
                <Typography variant="h4" color="error">
                  {reportData.summary?.inactiveLandlords || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card>
              <CardContent>
                <Typography variant="h6">Churn Rate</Typography>
                <Typography variant="h4" color={reportData.summary?.churnRate > 10 ? 'error' : 'warning'}>
                  {reportData.summary?.churnRate || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6">Total Properties</Typography>
                <Typography variant="h4">{reportData.summary?.totalProperties || 0}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6">Active Properties</Typography>
                <Typography variant="h4" color="success.main">
                  {reportData.summary?.activeProperties || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6">Active Property Rate</Typography>
                <Typography variant="h4">
                  {reportData.summary?.activePropertyRate || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {reportData.propertiesGained && reportData.propertiesGained.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>Properties Gained</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell>Landlord</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.propertiesGained.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.propertyName}</TableCell>
                      <TableCell>{item.landlordName}</TableCell>
                      <TableCell>{new Date(item.gainedDate).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
          </Box>
        )}

        {reportData.propertiesLost && reportData.propertiesLost.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom color="error">Properties Lost</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Landlord</TableCell>
                    <TableCell>Properties</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.propertiesLost.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{item.landlordName}</TableCell>
                      <TableCell>{item.properties.map(p => p.propertyName).join(', ')}</TableCell>
                      <TableCell>{new Date(item.lostDate).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ScrollTable>
          </Box>
        )}
      </Box>
    );
  };

  const renderAgencyFeesCollections = () => {
    if (!reportData) return null;

    return (
      <Box>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Total Agency Fees Collected</Typography>
            <Typography variant="h3" color="success.main">
              {formatCurrency(reportData.totalCollected || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {reportData.transactionCount || 0} fee collection transaction(s)
            </Typography>
          </CardContent>
        </Card>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>By Landlord</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Landlord</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Transactions</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Total Fees</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.byLandlord && reportData.byLandlord.length > 0 ? (
                    reportData.byLandlord.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.landlordName || 'Unknown'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{item.transactionCount || 0}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                          {formatCurrency(item.totalFees || 0)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">No data</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ScrollTable>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>By Property</Typography>
            <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Transactions</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Total Fees</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.byProperty && reportData.byProperty.length > 0 ? (
                    reportData.byProperty.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{item.propertyName || 'Unknown'}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{item.transactionCount || 0}</TableCell>
                        <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                          {formatCurrency(item.totalFees || 0)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={3} align="center">No data</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </ScrollTable>
          </Grid>
        </Grid>

        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>Fee Collection Transactions</Typography>
        <ScrollTable maxHeight={EMBEDDED_MAX_HEIGHT} containerProps={{ component: Paper }} size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Property</TableCell>
                <TableCell>Landlord</TableCell>
                <TableCell>Receipt Number</TableCell>
                <TableCell>Payment Date</TableCell>
                <TableCell>Payment Amount</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>Fee Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {reportData.transactions && reportData.transactions.length > 0 ? (
                reportData.transactions.map((transaction, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      {transaction.entryDate ? new Date(transaction.entryDate).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell>{transaction.propertyName || 'N/A'}</TableCell>
                    <TableCell>{transaction.landlordName || 'N/A'}</TableCell>
                    <TableCell>{transaction.receiptNumber || '-'}</TableCell>
                    <TableCell>
                      {transaction.paymentDate ? new Date(transaction.paymentDate).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace' }}>
                      {transaction.paymentAmount ? formatCurrency(transaction.paymentAmount) : '-'}
                    </TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 500, color: 'success.dark' }}>
                      {formatCurrency(transaction.amount || 0)}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center">No fee collection transactions found</TableCell>
                </TableRow>
              )}
            </TableBody>
          </ScrollTable>
      </Box>
    );
  };

  const renderOutstandingCollection = () => {
    if (!reportData?.landlords) return null;

    const { period, totals, otherRevenues, reportTotal } = reportData;
    // The billed-in-period column is captioned with the period itself, so a filed copy
    // still says which month it is for.
    const chargeHeading = period?.label || 'Selected period';

    const AMOUNT_FIELDS = ['debt', 'charge', 'total', 'payment', 'advance', 'totalCollection', 'revenue', 'balance'];

    const amountCells = (source, sx = {}) => AMOUNT_FIELDS.map((field) => (
      <TableCell key={field} align="right" sx={sx}>
        {formatCurrency(source[field] || 0)}
      </TableCell>
    ));

    // Other revenues are agency-wide and arise on no particular letting, so a month can
    // have penalty or other income with no rent activity behind it at all. Bailing out on
    // the groups alone hid real money behind "nothing to report".
    if (reportData.landlords.length === 0 && !(otherRevenues?.total)) {
      return (
        <Typography color="text.secondary">
          Nothing collected or outstanding for {chargeHeading}.
        </Typography>
      );
    }

    return (
      <Box>
        <ScrollTable size="small">
          <TableHead>
            <TableRow>
              <TableCell className="pin-left">Unit No</TableCell>
              <TableCell>Customer Name</TableCell>
              <TableCell align="right">Debt</TableCell>
              <TableCell align="right">{chargeHeading}</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell align="right">Payment</TableCell>
              <TableCell align="right">Advance Payment</TableCell>
              <TableCell align="right">Total Collection</TableCell>
              <TableCell align="right">Revenue</TableCell>
              <TableCell align="right">Balance</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData.landlords.map((landlord) => (
              <React.Fragment key={landlord.landlordId || landlord.landlordName}>
                <TableRow>
                  <TableCell className="pin-left" colSpan={2} sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                    {landlord.landlordName}
                  </TableCell>
                  <TableCell colSpan={8} sx={{ bgcolor: 'action.hover' }} />
                </TableRow>

                {landlord.properties.map((property) => (
                  <React.Fragment key={property.propertyId}>
                    <TableRow>
                      <TableCell className="pin-left" colSpan={2} sx={{ pl: 3, fontStyle: 'italic' }}>
                        {property.propertyName}
                      </TableCell>
                      <TableCell colSpan={8} />
                    </TableRow>

                    {property.rows.map((row) => (
                      <TableRow key={row.tenantId}>
                        <TableCell className="pin-left">{row.unitNumber || '-'}</TableCell>
                        <TableCell>
                          {row.tenantName}
                          {!row.isCurrentTenant && (
                            <Chip label="Past tenant" size="small" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        {amountCells(row)}
                      </TableRow>
                    ))}

                    <TableRow>
                      <TableCell className="pin-left" colSpan={2} sx={{ pl: 3, fontWeight: 'bold' }}>
                        {property.propertyName} total
                      </TableCell>
                      {amountCells(property.subtotal, { fontWeight: 'bold' })}
                    </TableRow>
                  </React.Fragment>
                ))}

                <TableRow>
                  <TableCell className="pin-left" colSpan={2} sx={{ fontWeight: 'bold', bgcolor: 'action.hover' }}>
                    {landlord.landlordName} total
                  </TableCell>
                  {amountCells(landlord.subtotal, { fontWeight: 'bold', bgcolor: 'action.hover' })}
                </TableRow>
              </React.Fragment>
            ))}

            {/* Not grouped by landlord or property, because they are not: bank interest and
                a bridge-financing fee arise on no particular building. Only the Revenue
                column carries a figure, and the rows add into the total beneath. */}
            <TableRow>
              <TableCell className="pin-left" colSpan={2} sx={{ fontWeight: 'bold', pt: 3, textDecoration: 'underline' }}>
                Add other revenues
              </TableCell>
              {AMOUNT_FIELDS.map((field) => (
                <TableCell key={field} align="right" sx={{ fontWeight: 'bold', pt: 3 }}>
                  {field === 'revenue' ? formatCurrency(otherRevenues?.commission || 0) : ''}
                </TableCell>
              ))}
            </TableRow>

            {(otherRevenues?.lines || []).map((line) => (
              <TableRow key={line.key}>
                <TableCell className="pin-left" colSpan={2} sx={{ pl: 3 }}>{line.label}</TableCell>
                {AMOUNT_FIELDS.map((field) => (
                  <TableCell key={field} align="right">
                    {field === 'revenue' ? formatCurrency(line.amount || 0) : ''}
                  </TableCell>
                ))}
              </TableRow>
            ))}

            <TableRow>
              <TableCell className="pin-left" colSpan={2} sx={{ fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' }}>
                Total
              </TableCell>
              {amountCells(reportTotal || totals || {}, { fontWeight: 'bold', borderTop: '2px solid', borderColor: 'divider' })}
            </TableRow>
          </TableBody>
        </ScrollTable>

        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Debt is rent carried forward from earlier months and still unpaid. The month
          column is that month's rent still to clear, so a month paid for in advance shows
          nothing. Payment is money received during the period; Balance is Total less
          Payment, and carries forward as next month&apos;s Debt. Other revenues are agency
          income for the whole period and are not split by landlord or property.
          {otherRevenues?.note ? ` ${otherRevenues.note}` : ''}
        </Typography>
      </Box>
    );
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Agency Reports</Typography>
      
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          {activeTab === 0 && (
            <Grid item xs={12} md={3}>
              <TextField
                label="Age Threshold (days)"
                type="number"
                value={ageThreshold}
                onChange={(e) => setAgeThreshold(parseInt(e.target.value) || 30)}
                fullWidth
              />
            </Grid>
          )}
          {(activeTab === 1 || activeTab === 2 || activeTab === 3 || activeTab === 5 || activeTab === 6 || activeTab === 8 || activeTab === 9) && (
            <>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Landlord (Optional)</InputLabel>
                  <Select
                    value={selectedLandlord}
                    onChange={(e) => {
                      setSelectedLandlord(e.target.value);
                      setSelectedProperty('');
                    }}
                  >
                    <MenuItem value="">All Landlords</MenuItem>
                    {landlords.map((landlord) => (
                      <MenuItem key={landlord._id} value={landlord._id}>
                        {landlord.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Property (Optional)</InputLabel>
                  <Select
                    value={selectedProperty}
                    onChange={(e) => setSelectedProperty(e.target.value)}
                    disabled={!selectedLandlord && properties.length === 0}
                  >
                    <MenuItem value="">All Properties</MenuItem>
                    {properties.map((property) => (
                      <MenuItem key={property._id} value={property._id}>
                        {property.propertyName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </>
          )}
          {(activeTab === 3 || activeTab === 5 || activeTab === 6 || activeTab === 7 || activeTab === 8 || activeTab === 9) && (
            <>
              <Grid item xs={12} md={2}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} md={2}>
            <Button
              variant="contained"
              fullWidth
              onClick={handleGenerateReport}
              disabled={loading}
            >
              Generate
            </Button>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
            // WRAPPED ONTO AS MANY ROWS AS IT TAKES, not scrolled.
            //
            // The default variant clips whatever does not fit, silently — a report that
            // exists simply is not there. Scrolling fixes the clipping but not the
            // discovery: arrows still hide most of the list from somebody who has never
            // seen this screen. Wrapping costs one row of height and shows every report at
            // once, which is the point of a tab bar.
            sx={{
              '& .MuiTabs-flexContainer': { flexWrap: 'wrap' },
              // The sliding indicator is absolutely positioned along the bottom of the whole
              // control and knows only the selected tab's HORIZONTAL offset, so once the
              // tabs wrap it underlines whatever sits below the selection. The selected tab
              // carries its own underline instead.
              '& .MuiTabs-indicator': { display: 'none' },
              '& .MuiTab-root': {
                borderBottom: '2px solid transparent',
                '&.Mui-selected': { borderBottomColor: 'primary.main' }
              }
            }}
            value={activeTab}
            onChange={(e, newValue) => {
              setActiveTab(newValue);
              // Each report has its own shape and they share one `reportData`. Left in
              // place, the next tab renders the last tab's payload through the wrong
              // renderer - which for two reports that both return `landlords` is not a
              // blank screen but a crash.
              setReportData(null);
            }}
          >
          <Tab label="Dashboard Summary" />
          <Tab label="Tenant Statistics" />
          <Tab label="Landlord Portfolio" />
          <Tab label="Payments Report" />
          <Tab label="Arrears Report" />
          <Tab label="Management Fee Revenue" />
          <Tab label="Maintenance Efficiency" />
          <Tab label="Portfolio Churn" />
          <Tab label="Agency Fees Collections" />
          <Tab label="Outstanding Collection" />
        </Tabs>
      </Box>

      {reportData && (
        <Box sx={{ mb: 2 }}>
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button startIcon={<PrintIcon />} variant="outlined" onClick={() => window.print()}>Print</Button>
            {/* No PDF generator exists for the agency reports yet. Shown disabled rather than
                removed, so the gap is visible and the layout matches the landlord screen. */}
            <Button startIcon={<DownloadIcon />} variant="outlined" disabled title="PDF export is not available for agency reports yet">Download PDF</Button>
            <Button startIcon={<DownloadIcon />} variant="outlined" onClick={handleDownloadExcel}>Download Excel</Button>
          </Box>
        </Box>
      )}

      {reportData && (
        <Box>
          {activeTab === 0 && renderDashboardSummary()}
          {activeTab === 1 && renderTenantStatistics()}
          {activeTab === 2 && renderLandlordPortfolio()}
          {activeTab === 3 && renderPayments()}
          {activeTab === 4 && renderArrears()}
          {activeTab === 5 && renderManagementFeeRevenue()}
          {activeTab === 6 && renderMaintenanceEfficiency()}
          {activeTab === 7 && renderPortfolioChurn()}
          {activeTab === 8 && renderAgencyFeesCollections()}
          {activeTab === 9 && renderOutstandingCollection()}
        </Box>
      )}
    </Paper>
  );
};

export default AgencyReports;

